<template>
  <div>
    <div v-if="!hasPermission" class="no-permission">
      <i class="far fa-frown fa-2x"></i>
      <div class="message">You don't have permission to access the driver payouts.</div>
    </div>
    <div v-else>
      <div class="payouts">
        <div class="payout-account">
          <div class="title">
            <div v-if="payoutAccount !== null && payoutAccount.defaultPayoutMethod !== null" class="pay-div">
              <div>
                <i class="fa fa-check-circle"></i>
                <span>Auto-Deposit Enabled</span>
              </div>
              <span class="default-payout">
                <i class="fa fa-university" v-show="payoutAccount.defaultPayoutMethod.accountType === 'bank_account'"></i>
                <i class="fa fa-credit-card" v-show="payoutAccount.defaultPayoutMethod.accountType === 'card'"></i>
                <span>{{ payoutAccount.defaultPayoutMethod.accountBrand }}</span>
                <span>({{ payoutAccount.defaultPayoutMethod.lastFour }})</span>
              </span>
              <a class="link" :href="payoutAccount.execRideUrl" target="_blank">
                <span>Executive Ride</span>
                <i class="fa fa-external-link-alt"></i>
              </a>
              <a class="link" :href="payoutAccount.firstClassUrl" target="_blank">
                <span>First Class Transportation</span>
                <i class="fa fa-external-link-alt"></i>
              </a>
            </div>
            <div v-else="payoutAccount === null">
              <i class="fa fa-times-circle"></i>
              <span>Auto-Deposit Disabled</span>
            </div>
          </div>
        </div>

        <div class="payout-details">
          <div class="batch-info">
            <div class="details-box">
              <div class="batch-title" v-if="filter.selectedWeek !== null">
                <span>Payout for</span>
                <span class="value" v-if="!filter.selectedWeek.isCustom">
                  {{ filter.selectedWeek.start ? filter.selectedWeek.start.format('MMMM DD') : '' }} - 
                  {{ filter.selectedWeek.end ? filter.selectedWeek.end.format('MMMM DD') : '' }}
                </span>
                <span v-else>
                  {{ formatDate(startDate) }} -
                  {{ formatDate(endDate) }}
                </span>
                <!--span class="schedule">Scheduled Payout:</span>
                <span class="value">August 12 - 08:00AM</span-->
              </div>

              <div class="amount">
                <div class="key-value">
                  <span class="box-icon credit"></span>
                  <span>Credit</span>
                  <span class="value">${{ batch.credit.toFixed(2) }}</span>
                </div>
                <div class="key-value">
                  <span class="box-icon debit"></span>
                  <span>Debit</span>
                  <span class="value">- (${{ batch.debit.toFixed(2) }})</span>
                </div>
                <div class="separator"></div>
                <div class="key-value total">
                  <span class="box-icon total"></span>
                  <span>Total</span>
                  <span class="value">${{ (batch.credit - batch.debit).toFixed(2) }}</span>
                </div>
              </div>

              <div class="batch-status" v-if="batch.item !== null">
                <div v-if="batch.item.payoutStatus === 'paid'">
                  <i class="fa fa-check-circle fa-2x"></i>
                  <div class="message">This batch was already paid to the driver.</div>
                </div>
                <div v-if="batch.item.payoutStatus === 'pending'">
                  <i class="fa fa-exclamation-circle fa-2x"></i>
                  <div class="message">This batch is not paid yet.</div>
                </div>
                <div v-if="batch.item.payoutStatus === 'in_transit'">
                  <i class="fa fa-exclamation-circle fa-2x"></i>
                  <div class="message">The payment for this batch is on the way to the driver's account.</div>
                </div>
                <div v-if="batch.item.payoutStatus === 'failed'">
                  <i class="fa fa-times-circle fa-2x"></i>
                  <div class="message">Something went wrong for the payout of this batch.</div>
                </div>
                <div v-if="batch.item.payoutStatus === 'canceled'">
                  <i class="fa fa-times-circle fa-2x"></i>
                  <div class="message">This payout batch has been cancelled.</div>
                </div>
              </div>
            </div>

            <div>
              <div class="batch-filter" v-if="filter.weeks !== null">
                <div class="select-input">
                  <select v-model="filter.selectedWeek" @change="initTransactions">
                    <option v-for="(week, index) in filter.weeks" :value="week" :key="index">
                      {{ week.start.format('MMM DD') }} - {{ week.end.format('MMM DD') }}
                    </option>
                    <option :value="customVal">Custom Date</option>
                  </select>
                </div>
                <div class="date-range" v-if="filter.selectedWeek.isCustom">
                  <div>
                    <datepicker format="MMM dd" placeholder="Start" v-on:input="changeDateRange"
                      :class="['datepicker inline rtl-dp']" input-class="dtp"
                      v-model="startDate" :disabled-picker="downloading"></datepicker>
                  </div>
                  <div>
                    <datepicker format="MMM dd" placeholder="End" v-on:input="changeDateRange"
                      :class="['datepicker inline rtl-dp', {'has-error':!!validation.startDate}]" input-class="dtp"
                      v-model="endDate" :disabled-picker="downloading"></datepicker>
                  </div>
                </div>
                <span v-if="!!validation.startDate" class="error-msg">{{validation.startDate}}</span>

                <a v-bind:class="{ 'batch-btn button cta xsmall': true, disabled: downloading }"
                  href="javascript:;" v-show="transactions != null && transactions.length > 0" @click="dlModal = true">
                  <i class="fa fa-print"></i>
                  <span>DOWNLOAD RECEIPT</span>
                </a>
                <a class="batch-btn button cta xsmall success" href="javascript:;" @click="showPayoutForm(null)" v-show="isCurrentBatch">
                  <i class="fa fa-plus"></i>
                  <span>ADD TRANSACTION</span>
                </a>

                <a v-bind:class="{ 'batch-btn button cta xsmall success': true, disabled: batch.updating }" href="javascript:;" @click="markAsPaid(batch.item)"
                  v-if="batch.item !== null && batch.item.payoutStatus !== 'paid' && batch.item.providerExternalAccountId === null && batch.item.amount > 0">
                  CHECK ISSUED
                </a>
              </div>
            </div>
          </div>


          <div class="batch-transactions">
            <div class="loading-trx" v-show="loadingTransactions">
              <i class="fa fa-spinner fa-spin fa-lg"></i><span>LOADING TRANSACTIONS...</span>
            </div>
            <div v-show="!loadingTransactions">
              <div class="empty-transactions" v-if="transactions !== null && transactions.length <= 0">
                <i class="fa fa-folder-open fa-2x"></i>
                <div class="message">This driver does not have any transaction for this period.</div>
              </div>
              <div v-else>
                <div class="title">BATCH TRANSACTIONS</div>
                <div v-bind:class="{ transaction: true, 'no-controls': !isCurrentBatch }" v-for="(trx, index) in transactions">
                  <div class="date">{{ trx.created }}</div>
                  <div v-bind:class="[ 'type', trx.type ]">{{ trx.type }}</div>
                  <div class="description">
                    <span v-if="trx.reservationId === null && trx.packageId === null">{{ trx.origDescription }}</span>
                    <span v-if="trx.reservationId !== null || trx.packageId !== null">{{ trx.description }}</span>
                    <a :href="'/#/reservation/' + trx.reservationId" target="_blank" v-if="trx.reservationId !== null">Reservation {{ trx.reservationId }}</a>
                    <a :href="'/#/package/' + trx.packageId" target="_blank" v-if="trx.packageId !== null">Package {{ trx.packageId }}</a>
                  </div>
                  <a class="note" href="javascript:;" v-show="trx.notes !== null && trx.notes !== ''" @click="showNotes(trx)">
                    <i class="fa fa-comment-dots"></i>
                  </a>
                  <div class="amount">
                    <span v-if="trx.type === 'credit'">${{ trx.amount.toFixed(2) }}</span>
                    <span v-else>- (${{ trx.amount.toFixed(2) }})</span>
                  </div>
                  <div class="controls" v-show="isCurrentBatch">
                    <a class="button cta xsmall" href="javascript:;" @click="showPayoutForm(trx)">EDIT</a>
                    <a class="button cta error xsmall" href="javascript:;" @click="toggleRemovePrompt(index)">REMOVE</a>
                  </div>
                  
                  <transition name="fade">
                    <div class="remove-prompt" v-show="trx.showRemove">
                      <div v-show="!removingTransaction">
                        <div class="message">Are you sure you want to remove this transaction?</div>
                        <div class="remove-controls">
                          <a class="button cta xsmall warning" href="javascript:;" @click="removeTransaction(index)">YES</a>
                          <a class="button cta xsmall" href="javascript:;" @click="toggleRemovePrompt(index)">NO</a>
                        </div>
                      </div>
                      <div v-show="removingTransaction">
                        <div class="removing-message">
                          <i class="fa fa-spinner fa-spin"></i>
                          <span>REMOVING TRANSACTION...</span>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['lr-modal', {'hide': !dlModal}]">
      <div class="lr-box">
        <div class="lr-alert-msg" style="margin-bottom: 30px;">
          <b>Download receipt:</b>
          <div class="d-flex justify-space-around">
            <label>
              <input type="radio" v-model="dlVia" value="pdf" />
              PDF
            </label>
            <label>
              <input type="radio" v-model="dlVia" value="csv" />
              CSV
            </label>
          </div>
        </div>
        <a class="button success small block" @click="initDownload">Download</a>
        <a class="button small secondary block" style="margin-top: 10px" @click="dlModal = false">
          Cancel
        </a>
      </div>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import moment from 'moment'
import datepicker from 'vuejs-datepicker'
import CsvExport from 'csvexport'
import ApiPayouts from '../../modules/api/provider/payouts'

export default {
  name: 'payouts',
  props: [ 'active' ],
  components: { datepicker },
  data () {
    return {
      downloading: false,
      filter: {
        weeks: [],
        selectedWeek: {isCustom: false}
      },
      payoutAccount: null,
      loadingTransactions: false,
      transactions: null,
      removingTransaction: false,
      batch: {
        loading: false,
        item: null,
        updating: false,
        debit: 0,
        credit: 0
      },
      loaded: '',
      customVal: {
        start: new Date(),
        end: new Date(),
        isCustom: true
      },
      startDate: '',
      endDate: '',
      validation: {
        startDate: '',
        endDate: ''
      },
      dlVia: 'pdf',
      dlModal: false
    }
  },
  computed: {
    provId () { return this.$route.params.id },
    hasPermission () {
      return typeof this.$store.state.session.user.access.PageAdminDriverBatchModifications !== 'undefined'
    },
    isCurrentBatch () {
      if (this.filter.selectedWeek === null || this.filter.weeks.length <= 0 || this.filter.selectedWeek.isCustom) {
        return false
      }
      return this.filter.selectedWeek.start.isSame(this.filter.weeks[0].start)
    },
    refreshTrx () { return this.$store.state.provider.payoutForm.refresh }
  },
  methods: {
    init () {
      let self = this
      let start = moment().startOf('isoweek')
      let end = moment(start).endOf('isoweek')
      self.filter.weeks = []
      self.filter.weeks.push({
        start: start,
        end: end
      })
      self.filter.selectedWeek = self.filter.weeks[0]
      let current = moment(start).startOf('isoweek')
      for (let i = 0; i < 7; i++) {
        let startWeek = moment(current).subtract(1, 'd').startOf('isoweek')
        let endWeek = moment(startWeek).endOf('isoweek')
        current = moment(startWeek)

        self.filter.weeks.push({
          start: startWeek,
          end: endWeek
        })
      }
      self.initTransactions()
      ApiPayouts.getAccount(self, self.provId, r => {
        if (r.error) {
          self.payoutAccount = null
          return
        }
        if (r.account === null) {
          self.payoutAccount = null
          return
        }
        self.payoutAccount = {
          id: r.account.id,
          execRideUrl: CONFIG.Stripe.accountURL + r.account.executiveRideStripeAccountId,
          firstClassUrl: CONFIG.Stripe.accountURL + r.account.firstClassTransportationStripeAccountId,
          rejected: r.account.rejected === 1,
          deleted: r.account.deleted === 1,
          payoutMethods: r.externalAccounts,
          defaultPayoutMethod: r.externalAccounts.length > 0 ? r.externalAccounts.find(e => {
            return e.default === 1
          }) : null
        }
      })
    },
    initBatch () {
      let self = this
      self.batch.loading = true
      self.batch.item = null
      let since = ''
      let until = ''
      if (self.filter.selectedWeek.isCustom) {
        since = moment(self.startDate).format('YYYY-MM-DD')
        until = moment(self.endDate).format('YYYY-MM-DD')
      } else {
        since = self.filter.selectedWeek.start.format('YYYY-MM-DD')
        until = self.filter.selectedWeek.end.format('YYYY-MM-DD')
      }
      ApiPayouts.getProviderBatch(self, {
        token: self.$store.state.session.sessionToken,
        contactId: self.provId,
        since: since,
        until: until
      }, r => {
        self.batch.loading = false
        if (r.error) {
          return
        }

        if (r.list.length <= 0) {
          self.batch.item = null
          return
        }
        self.batch.item = r.list[0]
      })
    },
    initTransactions () {
      let self = this
      let since = ''
      let until = ''
      if (self.filter.selectedWeek.isCustom) {
        if (!self.startDate || !self.endDate) return
        since = moment(self.startDate).format('YYYY-MM-DD')
        until = moment(self.endDate).format('YYYY-MM-DD')
      } else {
        self.validation.startDate = ''
        self.startDate = ''
        self.endDate = ''
        since = self.filter.selectedWeek.start.format('YYYY-MM-DD')
        until = self.filter.selectedWeek.end.format('YYYY-MM-DD')
      }

      self.initBatch()
      self.loadingTransactions = true
      ApiPayouts.getTransactions(self, {
        token: self.$store.state.session.sessionToken,
        contactId: self.provId,
        deleted: 0,
        since: since,
        until: until,
        order: 'created',
        orderAsc: 0
      }, r => {
        self.loadingTransactions = false
        self.$store.commit('provider/setPayoutFormRefresh', false)
        if (r.error) {
          self.transactions = null
          return
        }
        self.transactions = self.processTransactions(r.list)
      })
    },
    processTransactions (transactions) {
      let result = []
      let self = this
      self.batch.credit = 0
      self.batch.debit = 0
      for (let i = 0; i < transactions.length; i++) {
        let trx = transactions[i]
        let created = moment(trx.created.substring(0, 10) + ' ' + trx.created.substring(11, 19), 'YYYY-MM-DD HH:mm:ss')
        trx.created = created.format('MM/DD/YYYY hh:mm A')
        trx['origDescription'] = trx.description
        let descriptionIndex = -1
        if (trx.reservationId !== null) {
          descriptionIndex = trx.description.indexOf('Reservation')
        }
        if (trx.packageId !== null) {
          descriptionIndex = trx.description.indexOf('Package')
        }
        if (descriptionIndex >= 0) {
          trx.description = trx.description.substring(0, descriptionIndex)
        }

        if (trx.type === 'credit') {
          self.batch.credit += trx.amount
        } else {
          self.batch.debit += trx.amount
        }

        trx['showRemove'] = false
        result.push(trx)
      }
      return result
    },
    toggleRemovePrompt (index) {
      let self = this
      self.transactions[index].showRemove = !self.transactions[index].showRemove
    },
    removeTransaction (index) {
      let self = this
      let trx = self.transactions[index]
      self.removingTransaction = true
      ApiPayouts.updateTransaction(self, trx.id, {
        token: self.$store.state.session.sessionToken,
        type: trx.type,
        transactionType: trx.transactionType,
        deleted: 1
      }, r => {
        self.transactions[index].showRemove = false
        self.removingTransaction = false
        if (r.error) {
          return
        }
        self.initTransactions()
      })
    },
    showPayoutForm (trx) {
      if (trx === null) {
        this.$store.commit('provider/setPayoutFormNew')
      } else {
        this.$store.commit('provider/setPayoutFormForm',{
          id: trx.id,
          type: trx.type,
          transactionType: trx.transactionType,
          amount: trx.amount,
          description: trx.origDescription,
          notes: trx.notes,
          reservationId: trx.reservationId,
          packageId: trx.packageId
        })

        
      }
      this.$store.commit('provider/setPayoutFormShow', true)
    },
    showNotes (trx) {
      let self = this
      self.$store.commit('provider/setPayoutNotes', {
        show: true,
        note: trx.notes
      })
    },
    markAsPaid (batch) {
      let self = this
      self.batch.updating = true
      ApiPayouts.updateBatch(self, batch.id, {
        token: self.$store.state.session.sessionToken,
        payoutStatus: 'paid'
      }, r => {
        self.batch.updating = false
        if (r.error) {
          return
        }
        batch.payoutStatus = 'paid'
        self.batch.item = batch
      })
      console.log('PAID', batch)
    },
    printReciept () {
      let self = this
      self.downloading = true
      let since = ''
      let until = ''
      if (self.filter.selectedWeek.isCustom) {
        since = moment(self.startDate)
        until = moment(self.endDate)
      } else {
        since = self.filter.selectedWeek.start
        until = self.filter.selectedWeek.end
      }
      self.$sdk.download('/provider/transaction/print', {
        token: self.$store.state.session.sessionToken,
        contactId: self.provId,
        deleted: 0,
        since: since.format('YYYY-MM-DD'),
        until: until.format('YYYY-MM-DD'),
        order: 'created',
        orderAsc: 0
      }, data => {
        self.downloading = false
        let a = document.createElement('a')
        document.body.appendChild(a)
        a.style = 'display: none'
        a.href = data
        a.download = since.format('MMDDYY') + ' - ' + until.format('MMDDYY') + '.pdf'
        a.click()
        window.URL.revokeObjectURL(data)
        document.body.removeChild(a)
      }, '2.0')
    },
    calendarFormat (date) {
      return moment(date).format('MM/DD/YYYY')
    },
    changeDateRange () {
      if (!this.startDate || !this.endDate) return
      this.validation.startDate = ''
      let start = moment(this.startDate)
      let end = moment(this.endDate)
      if (end.diff(start, 'days') < 0) {
        this.validation.startDate = 'Invalid date range.'
        return
      }
      this.initTransactions()
    },
    formatDate (x) {
      if (!x) return ''
      return moment(x).format('MMM DD')
    },
    initDownload () {
      if (this.dlVia == 'pdf') this.printReciept()
      else this.printCsv() 
      this.dlModal = false
    },
    printCsv () {
      let since = ''
      let until = ''
      if (this.filter.selectedWeek.isCustom) {
        since = moment(this.startDate)
        until = moment(this.endDate)
      } else {
        since = this.filter.selectedWeek.start
        until = this.filter.selectedWeek.end
      }

      const exporter = CsvExport.create({
        filename: `${since.format('MMDDYY')} - ${until.format('MMDDYY')}.csv`,
        columns: ['Date', 'Transaction', 'Amount']
      })
      let data = []
      let provData = this.$store.state.provider

      data.push({
        Date: provData.businessInfo.name,
        Transaction: '',
        Amount: 'INVOICE'
      })
      data.push({
        Date: provData.homeAdress.address,
        Transaction: '',
        Amount: `${since.format('MM/DD/YYYY')} - ${until.format('MM/DD/YYYY')}`
      })
      data.push({
        Date: `${provData.homeAdress.city}, ${provData.homeAdress.state} ${provData.homeAdress.zip}`,
        Transaction: '',
        Amount: ''
      })
      data.push({Date: '', Transaction: '', Amount: ''})
      data.push({Date: '', Transaction: '', Amount: ''})

      let credit = 0
      let debit = 0
      this.transactions.forEach(trx => {
        if (trx.type === 'credit') credit += trx.amount
        else debit += trx.amount
        data.push({
          Date: moment(trx.created, 'MM/DD/YYYY hh:mm A').format('MMM DD, YYYY H:mm A'),
          Transaction: trx.origDescription,
          Amount: '$' + trx.amount
        })
      })
      data.push({Date: '', Transaction: '', Amount: ''})
      data.push({Date: '', Transaction: 'Credit:', Amount: '$' + credit})
      data.push({Date: '', Transaction: 'Debit:', Amount: `-($${debit})`})
      data.push({Date: '', Transaction: 'Total:', Amount: '$' + (credit - debit)})
      exporter.downloadCsv(data)
    }
  },
  watch: {
    provId (value) {
      if (this.active && this.loaded !== value) {
        this.loaded = this.provId
        this.init()
      }
    },
    refreshTrx (value) {
      if (!value) {
        return
      }
      this.init()
    },
    active (val) {
      if (val && this.loaded !== this.provId) {
        this.loaded = this.provId
        this.init()
      }
    }
  },
  mounted () {
    // this.init()
  }
} 
</script>

<style lang="scss" scoped>
.payout-account {
  padding: 0;
  line-height: 14px;
  font-size: 14px;

  & > * {
    display: inline-block;
    vertical-align: middle;
  }

  & .link {
    margin-left: 10px;
  }

  & i.fa-times-circle { color: #F25E0D; }
  & i.fa-check-circle { color: #26D97A; }

  & .default-payout {
    color: #333;
    font-weight: 700;
    text-decoration: underline;
    margin: 0 15px 0 10px;
  }
}

.payout-details {
  margin-top: 15px;
  font-size: 14px;

  & .batch-info {
    position: relative;
    background-color: #fff;
    padding: 15px;
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;

    .details-box{
      width: 100%;
    }
    & .batch-title {
      color: #555;

      & .value { font-weight: 700; text-decoration: underline; }

      & .schedule { margin-left: 20px; }
    }

    & .amount {
      margin-top: 15px;
      line-height: 14px;
      & .key-value {
        padding: 5px 0;
        & > span { display: inline-block; vertical-align: middle; width: 60px; color: #555; }

        & > span.value { width: 140px; text-align: right; font-weight: 600; color: #444; }

        & > span.box-icon {
          width: 15px; height: 15px; background-color: red;

          &.credit { background-color: #26D97A; }
          &.debit { background-color: #F25E0D; }
          &.total { background-color: #0D79F2; }
        }

        &.total {
          padding-top: 10px;
          margin-top: 10px;
          width: 224px;
          font-size: 16px;
          line-height: 16px;
          border-top: 1px solid rgba(0,0,0,0.3);
          & .value { font-weight: 700; color: #0D79F2; font-size: 18px; }
        }

      }
    }

    & .batch-filter {
      width: 200px;
      // position: absolute;
      // top: 15px;
      // right: 15px;
      text-align: right;

      
      & select{
        margin-top: 5px;
        border: 1px solid #ccc;
        padding: 10px;
        color: #333;
        height: 40px;
        position: relative;
        font-size:14px;
        outline: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background: url('/assets/img/down.png') no-repeat 173px 9px;
        transition: all .35s ease;
      }

      & .batch-btn {
        display: block;
        margin-top: 10px;
      }
    }
  }
  & .batch-transactions {
    margin-top: 15px;
    background-color: #fff;
    padding: 15px;
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);

    & .title {
      font-size: 12px;
      font-weight: 700;
      color: #555;
      margin-bottom: 10px;
    }

    & .empty-transactions {
      text-align: center;
      color: #555;
      padding: 30px 0;
      & > i { color: #777; }
    }
  }

  .date-range{
    margin:5px 0 3px;
    display: flex;
    justify-content: space-between!important;
  }
  .error-msg{
    color: #f00;
    display: block;
    font-size: 13px;
    text-align: center;
  }

  & .transaction {
    position: relative;
    padding: 10px 0 12px;
    border-top: 1px solid rgba(0,0,0,0.2);

    & > .date,
    & > .type,
    & > .description,
    & > .note,
    & > .controls { vertical-align: middle; }

    & .date {
      font-size: 12px;
      line-height: 12px;
      font-weight: 700;
      color: #555;
      margin-top: 5px;
      margin-bottom: 5px;
    }
    & .type {
      font-size: 11px;
      line-height: 11px;
      text-transform: uppercase;
      font-weight: 700;
      color: #fff;
      display: inline-block;
      padding: 3px 5px 4px;
      border-radius: 3px;
      width: 50px;
      text-align: center;

      &.credit { background-color: #26D97A }
      &.debit { background-color: #F25E0D }
    }

    & .description {
      display: inline-block;
      margin-left: 10px;

      & a { font-weight: 700; }
    }

    & .note {
      color: #555;
      font-size: 16px;
      margin: 0 15px;
    }

    & .controls {
      position: absolute;
      right: 0;
      top: 20px;

      & .xsmall {
        font-size: 12px;
        padding: 5px 7px;
      }
    }

    & .amount {
      font-size: 18px;
      font-weight: 700;
      position: absolute;
      top: 20px;
      right: 130px;
    }

    &.no-controls .amount { right: 0; }

    & .remove-prompt {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      text-align: center;
      background-color: rgba(0,0,0,0.7);
      color: #fff;
      font-weight: 700;

      & .message {
        padding: 7px 0 5px;
      }
      & .xsmall {
        font-size: 12px;
        padding: 5px 7px;
        margin: 0 5px;
      }

      & .removing-message {
        padding: 20px 0;
        & > * {
          display: inline-block;
          vertical-align: middle
        }
      }
    }
  }
}

.dtp{
  padding: 0;
}

.batch-status {
  position: absolute;
  top: 40px;
  left: 300px;
  width: 400px;
  text-align: center;

  & .fa-check-circle { color: #26D97A; }
  & .fa-times-circle { color: #F20D0D; }
  & .fa-exclamation-circle { color: #F25E0D; }
  & .message {
    margin-top: 5px;
  }
}

.no-permission {
  text-align: center;
  color: #777;
  padding: 30px 0;
  & .message {
    margin-top: 10px;
    color: #555;
  }
}

.loading-trx {
  display: block;
  text-align: center;
  padding: 30px 0;
  font-size: 16px;
  font-weight: 700;
  color: #555;
  & > * {
    display: inline-block;
    vertical-align: middle;
  }

  & > span { margin-left: 10px; }
}

.lr-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99119;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.35);
  overflow: auto;
  opacity: 1;
  display: flex;
  justify-content: center!important;
  align-items: center!important;
  -webkit-transition: all 0.2s ease-in;
  -moz-transition: all 0.2s ease-in;
  -o-transition: all 0.2s ease-in;
  transition: all 0.2s ease-in;

  &.hide {
    top: -100%;
    opacity: 0;
  }
}
.lr-box {
  background: #fff;
  padding: 20px;
  border-radius: 6px;
  box-shadow: 3px 3px 2px rgba(0,0,0, .3);
  text-align: center;
  min-width: 300px;
  max-width: 500px;
}

.lr-alert-msg{
  margin-bottom: 20px;
  color: #45505b;

  input{
    width: auto;
    display: inline-block;
  }
}

@media only screen and (max-width: 430px) {
  .payouts {
    .payout-details {
      width: 90vw;
      .batch-info {
        .batch-filter {
          position: initial;
          margin: 0 auto;
          margin-top: 15px;
        }

        .batch-status {
          position: initial;
          width: auto;
        }

        .amount {
          .key-value {
            > span.value {
              width: 70%;
            }
            &.total {
              width: 100%;
            }
          }
        }
      }

      .transaction {
        .amount {
          position: initial;
          margin: 10px 0;
        }

        .controls {
          position: initial;
          .xsmall {
            padding: 8px 13px 9px;
          }
        }
      }
    } 
  }
}

@media only screen and (max-width: 430px) {
  .pay-div {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Provider/Payouts.vue